import type { Component, ViewerAPI } from '@wix/viewer-manager-interface'

const collectChildren = (structureMap: { [compId: string]: Component }, root: Component): Array<Component> => {
	// simple BFS
	const queue: Array<string> = []
	const result: Array<Component> = []

	queue.push(root.id)
	while (queue.length > 0) {
		const compId = queue.pop() as string
		if (compId in structureMap) {
			const childrenIds: Array<string> = structureMap[compId].components || ([] as Array<string>)
			queue.push(...childrenIds)
			childrenIds.forEach((childId) => {
				const child = structureMap[childId]
				if (child) {
					result.push(child)
				}
			})
		}
	}
	return result
}

const isTemplateComponent = (id: string) => id.toUpperCase() !== id

const normalizeQuery = (query?: string): string => query?.replace('#', '') || ''

export const createNoopUpdateStructureOptimizer = (
	_api: ViewerAPI
): ReturnType<typeof createUpdateStructureOptimizer> => {
	let api: ViewerAPI = _api
	return {
		getDebugInfo: () => ({}),
		reset: (_api) => {
			api = _api
		},
		onUpdateData: () => {},
		updateStructure: (id: string, component: any) => api.structure.updateStructure(id, component),
	}
}

/**
 * Optimizes calls to the api.structure.updateStructure by deferring calls to unused masterPage components until
 * an internal ref component is added, at which point the masterPage is populated with the deferred components (templates)
 *
 * assumptions
 * - Studio support only - masterPage components are irrelevant and are just templates
 * - Components updated in descending order (parent before children)
 * - Template components are registered before their consumer
 * - Data items are registered before their associated components
 */
export const createUpdateStructureOptimizer = (_api: ViewerAPI, debug = false) => {
	let api: ViewerAPI = _api

	let usedTemplates: { [templateId: string]: Set<string> } = {}
	// @ts-ignore
	let masterPageComponent: Component | null = null
	let templateStructure: { [compId: string]: Component } = {}
	let templateLinkMap: { [dataQuery: string]: string /* template id */ } = {}
	let internalRefComponents: { [compId: string]: Component } = {}

	const filterUnusedMasterPageChildren = (masterPage: Component): Component => {
		const components = masterPage.components?.filter((childId) => {
			return !isTemplateComponent(childId) || usedTemplates[childId]?.size
		})
		return { ...masterPage, components }
	}

	const getClosestUsedTemplateAncestor = (comp: Component): string => {
		let targetId = comp.id
		while (templateStructure[targetId]) {
			if (usedTemplates[targetId]?.size) {
				return targetId
			}
			targetId = templateStructure[targetId].parent!
		}
		return ''
	}

	const getTemplateComponent = (refComponent: Component): Component | undefined => {
		const query = normalizeQuery(refComponent.dataQuery)
		const rootCompId = templateLinkMap[query]
		return templateStructure[rootCompId]
	}

	return {
		getDebugInfo: () => {
			if (debug) {
				return {
					api,
					usedTemplates,
					masterPageComponent,
					templateStructure,
					templateLinkMap,
					internalRefComponents,
				}
			}
			return {}
		},
		reset: (_api: ViewerAPI) => {
			api = _api
			usedTemplates = {}
			// @ts-ignore
			masterPageComponent = null
			templateStructure = {}
			templateLinkMap = {}
			internalRefComponents = {}
		},
		onUpdateData: (type: any, id: string, item: any) => {
			if (item?.type === 'InternalRef') {
				templateLinkMap[id] = item.rootCompId
			}
		},
		updateStructure(id: string, component: Component | undefined) {
			if (component) {
				// add / update
				if (
					templateStructure[id] ||
					templateStructure[component.parent!] ||
					(component.parent === 'masterPage' && isTemplateComponent(id))
				) {
					templateStructure[id] = component
					usedTemplates[id] ??= new Set()

					const closestUsedTemplateAncestor = getClosestUsedTemplateAncestor(component)
					const isUnderUsedTemplateTree = Boolean(closestUsedTemplateAncestor)
					if (isUnderUsedTemplateTree) {
						// mark the template as used as well in case it isn't already
						usedTemplates[closestUsedTemplateAncestor].forEach((refComp) => {
							usedTemplates[id].add(refComp)
						})
					} else {
						// don't notify the Viewer when an unused template is added/modified
						return
					}
				} else if (id === 'masterPage') {
					// index templates in masterPage
					component.components?.forEach((childId) => {
						if (!templateStructure[childId] && isTemplateComponent(childId)) {
							templateStructure[childId] ??= {} as any // place a fake template root until we get the actual component in a separate call
							usedTemplates[childId] ??= new Set()
						}
					})
					masterPageComponent = component
					// forward only the necessary masterPage child components to the Viewer
					api.structure.updateStructure(id, filterUnusedMasterPageChildren(component))
					return
				} else if (component.type === 'RefComponent') {
					internalRefComponents[id] = component
					const templateComponent = getTemplateComponent(component)
					if (templateComponent) {
						// mark the template as used
						usedTemplates[templateComponent.id].add(id)
						if (templateComponent.parent === 'masterPage') {
							// add the template back to the masterPage children list now that we are sure it's used in the page
							api.structure.updateStructure('masterPage', filterUnusedMasterPageChildren(masterPageComponent!))
						}
						// add the template itself to the structure
						api.structure.updateStructure(templateComponent.id, templateComponent)
						// add the template's children tree (if the parent is used, they are also used)
						const templateSubtree = collectChildren(templateStructure, templateComponent)
						templateSubtree.forEach((child) => {
							usedTemplates[child.id].add(id)
							api.structure.updateStructure(child.id, child)
						})
					}
				}
			} else {
				// deletion flow
				if (internalRefComponents[id]) {
					// delete InternalRef component
					const comp = internalRefComponents[id]
					delete internalRefComponents[id]
					const templateComponent = getTemplateComponent(comp)
					delete templateLinkMap[comp.dataQuery!]
					if (templateComponent) {
						// release the template usage
						usedTemplates[templateComponent.id]?.delete(id)

						if (templateComponent.parent === 'masterPage') {
							// remove the template from the masterPage children list
							api.structure.updateStructure('masterPage', filterUnusedMasterPageChildren(masterPageComponent!))
						}
					}
				} else if (templateStructure[id]) {
					// delete template and associated template children
					const templateComp = templateStructure[id]
					const templateChildren = templateComp ? collectChildren(templateStructure, templateComp) : []

					const componentsToDelete = [templateComp, ...templateChildren]
					componentsToDelete.forEach((comp) => {
						// release the template usages and structure backup
						delete usedTemplates[comp.id]
						delete templateStructure[comp.id]
					})
				}
			}

			// @ts-ignore
			api.structure.updateStructure(id, component)
		},
	}
}
